import { useUserStorage } from '@common/hooks/useUserStorage';
import {
    useCallback, useEffect, useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import ConnectShopify, { CONNECT_SHOPIFY_STORAGE_KEY, ShopifyStore } from '../../components/ConnectShopify';
import GlobalBanner, { BannerConfig, BannerState } from '../../components/GlobalBanner';
import {
    ForNextPulseModal, PaySuccessModal, PricingModal
} from '../../components/Pricing';
import SettingsModal from '../../components/Settings/SettingsModal';
import { useAuth } from '../AuthContext';

export interface ModalConfig {
    isOpen: boolean;
    activeTab?: string;
    onClose?: () => void;
    data?: any;
}

export interface ModalState {
    modals: Record<string, ModalConfig>;
    openModal: (modalId: string, config?: Partial<ModalConfig>) => void;
    closeModal: (modalId: string) => void;
    toggleModal: (modalId: string, config?: Partial<ModalConfig>) => void;
    updateModal: (modalId: string, config: Partial<ModalConfig>) => void;
    isModalOpen: (modalId: string) => boolean;
    getModalConfig: (modalId: string) => ModalConfig;
}

export const modalInitialState: ModalState = {
    modals: {},
    openModal: () => {},
    closeModal: () => {},
    toggleModal: () => {},
    updateModal: () => {},
    isModalOpen: () => false,
    getModalConfig: () => ({ isOpen: false }),
};

export const modalInitialModalState: ModalState = modalInitialState;

export const createModalProvider = () => {
    const [modals, setModals] = useState<Record<string, ModalConfig>>({});
    const [banner, setBanner] = useState<BannerConfig | null>(null);
    const [stores, setStores] = useLocalStorage<ShopifyStore[]>(CONNECT_SHOPIFY_STORAGE_KEY, []);
    const { get, set } = useUserStorage();
    const { integrations, showGlobalLoading } = useAuth();
    const { hasConnectedIntegrations } = integrations;

    const openModal = useCallback((modalId: string, config?: Partial<ModalConfig>) => {
        // 特殊处理 forNextPulse modal：检查是否已经显示过
        if (modalId === 'forNextPulse') {
            const FOR_NEXT_PULSE_SHOWN_KEY = 'for_next_pulse_shown';
            const hasShown = get(FOR_NEXT_PULSE_SHOWN_KEY, false);
            if (hasShown) {
                return; // 已经显示过，不再打开
            }
            set(FOR_NEXT_PULSE_SHOWN_KEY, true);
        }

        setModals(prev => ({
            ...prev,
            [modalId]: {
                isOpen: true,
                activeTab: config?.activeTab,
                onClose: config?.onClose,
                data: config?.data,
                ...config,
            },
        }));
    }, [get, set]);

    const closeModal = useCallback((modalId: string) => {
        setModals(prev => {
            const modal = prev[modalId];
            if (modal?.onClose) {
                modal.onClose();
            }
            return {
                ...prev,
                [modalId]: {
                    ...modal,
                    isOpen: false,
                },
            };
        });
    }, []);

    const toggleModal = useCallback((modalId: string, config?: Partial<ModalConfig>) => {
        setModals(prev => {
            const modal = prev[modalId] || { isOpen: false };
            const newState = !modal.isOpen;
            return {
                ...prev,
                [modalId]: {
                    ...modal,
                    ...config,
                    isOpen: newState,
                },
            };
        });
    }, []);

    const updateModal = useCallback((modalId: string, config: Partial<ModalConfig>) => {
        setModals(prev => {
            const modal = prev[modalId] || { isOpen: false };
            return {
                ...prev,
                [modalId]: {
                    ...modal,
                    ...config,
                },
            };
        });
    }, []);

    const isModalOpen = useCallback((modalId: string) => {
        const modal = modals[modalId];
        return modal?.isOpen || false;
    }, [modals]);

    const getModalConfig = useCallback((modalId: string) => {
        return modals[modalId] || { isOpen: false };
    }, [modals]);

    // 处理 URL 参数自动打开 modal
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const connect_success = searchParams.get('connect_success');
        if (connect_success) {
            const channel = new BroadcastChannel('connect')

            channel.postMessage({
                type: 'SUCCESS',
                provider: connect_success,
            })

            window.close();
        }
    }, [searchParams]);

    // 处理支付结果 - 检查 URL 中的 pay_result 参数
    useEffect(() => {
        const pay_result = searchParams.get('pay_result');
        if (pay_result === 'success') {
            // 检查是否在 onboarding 流程中
            const isOnboarding = window.location.pathname === '/admin/onboarding';
            // // 打开欢迎弹窗，如果在 onboarding 流程中则启用 checking 状态
            if(isOnboarding){
                showGlobalLoading();
            }else{
                openModal('welcome');
            }
            // 清理 URL 参数
            window.history.replaceState({}, '', window.location.pathname);
        } else if (pay_result === 'cancel') {
            // 清理 URL 参数
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [searchParams, openModal, showGlobalLoading]);

    // 处理 Shopify store URL 参数
    useEffect(() => {
        const url = new URL(location.href);
        const storeUrl = url.searchParams.get('shop') || '';
        const storeToken = url.searchParams.get('shopify_token') || '';
        if (storeUrl && storeToken) {
            setStores(prev => [...prev.filter(v => v.storeUrl !== storeUrl), { storeUrl, storeToken }]);
        }
    }, [setStores]);

    // 处理 setting_modal URL 参数 - 打开 Settings modal 并指定 activeTab
    useEffect(() => {
        const settingModal = searchParams.get('setting_modal');
        if (settingModal) {
            // 打开 profile modal 并设置指定的 activeTab
            openModal('profile', { activeTab: settingModal });
            // 清理 URL 参数
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [searchParams, openModal]);

    // Banner 相关方法
    const showBanner = useCallback((config: BannerConfig) => {
        setBanner(config);
    }, []);

    const hideBanner = useCallback(() => {
        setBanner(null);
        // 存储用户已关闭过 banner 的标志
        set('connect_data_banner_closed', true);
    }, [set]);

    // 检查是否有任何已连接的集成，如果没有则显示 banner
    useEffect(() => {
        const { isLoading } = integrations;

        // 只在非加载状态时检查
        if (isLoading) {
            return;
        }

        // 检查用户是否已经关闭过 banner
        const hasClosedBanner = get('connect_data_banner_closed', false);
        if (hasClosedBanner) {
            return;
        }

        // 使用新的 boolean 值检查是否有连接（已排除 slack）
        if (!hasConnectedIntegrations) {
            // 清除之前的关闭记录，允许再次展示
            set('connect_data_banner_closed', false);
            // 没有连接任何数据源，显示 banner
            setBanner({
                content: (
                    <div className="flex items-center gap-2">
                        {/* Icons Group */}
                        <div className="flex items-center -space-x-1.5">
                            {(['shopify', 'amazon', 'google'] as const).map(
                                (source, idx) => {
                                    const domains: Record<string, string> = {
                                        shopify: 'shopify.com',
                                        amazon: 'amazon.com',
                                        google: 'google.com',
                                    };
                                    const domain = domains[source]
                                        || `${source}.com`;
                                    return (
                                        <div
                                            key={idx}
                                            className="w-6 h-6 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-sm ring-2 ring-white z-10 relative"
                                        >
                                            <img
                                                src={`https://cdn.brandfetch.io/${domain}`}
                                                alt={source}
                                                className="w-3.5 h-3.5 object-contain"
                                                onError={
                                                    (e) => e.currentTarget.style.display = 'none'
                                                }
                                            />
                                        </div>
                                    );
                                }
                            )}
                        </div>

                        {/* Text */}
                        <span className="text-[#1A162F] text-xs font-medium ml-2">
                            No data sources connected. Connect your platforms to
                            populate your Dashboard and Bustly Pulse.
                        </span>
                    </div>
                ),
                type: 'info',
                actionButton: {
                    text: 'Connect now',
                    onClick: () => {
                        openModal('profile', {activeTab: 'integration'});
                    },
                },
                showClose: true,
            });
        } else {
            // 有连接的集成，隐藏 banner
            setBanner(null);
        }
    }, [integrations, hasConnectedIntegrations, openModal, get, set]);

    const contextValue: ModalState & BannerState = {
        modals,
        openModal,
        closeModal,
        toggleModal,
        updateModal,
        isModalOpen,
        getModalConfig,
        banner,
        showBanner,
        hideBanner,
    };

    return contextValue;
};

// Modal Render Component - 接收 modal state 作为 props
interface ModalRenderProps {
    modalState: ModalState & BannerState;
}

export const ModalRender: React.FC<ModalRenderProps> = ({ modalState }) => {
    const pricingConfig = modalState.modals['pricing'] || { isOpen: false };
    const welcomeConfig = modalState.modals['welcome'] || { isOpen: false };
    const connectShopifyConfig = modalState.modals['connectShopify'] || { isOpen: false };
    const forNextPulseConfig = modalState.modals['forNextPulse'] || { isOpen: false };

    return (
        <>
            <GlobalBanner
                banner={modalState.banner}
                showBanner={modalState.showBanner}
                hideBanner={modalState.hideBanner}
            />
            <SettingsModal />
            {/* Deprecated 给自定义登录使用的 */}
            <ConnectShopify
                isOpen={connectShopifyConfig.isOpen}
                onClose={() => modalState.closeModal('connectShopify')}
            />
            <PricingModal
                isOpen={pricingConfig.isOpen}
                onClose={() => modalState.closeModal('pricing')}
                fisrt={false}
            />
            <PaySuccessModal
                isOpen={welcomeConfig.isOpen}
                onClose={() => modalState.closeModal('welcome')}
            />
            {/* Deprecated 给免费套餐引导支付的 */}
            <ForNextPulseModal
                isOpen={forNextPulseConfig.isOpen}
                onClose={() => modalState.closeModal('forNextPulse')}
            />
        </>
    );
};
